import { doc, onSnapshot, type DocumentData, type Unsubscribe } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * `districts/{districtId}` doc から `aiVerification` 設定 (DistrictAiVerificationConfig) のうち
 * user 側で必要な field だけを抽出して subscribe する hook (Sprint Y、free distribution UI 用)。
 *
 * - `enabled`: AI 検証機能全体の有効/無効
 * - `activeTemplateId`: 自由検証で使うアクティブ質問テンプレート ID
 * - `allowFreeVerification`: 自由検証 (admin が候補を指定せず、user が任意の AI 解析済投稿を選ぶ) を許可
 *
 * district doc は signed user に read 許可済 (firestore.rules canAccessDistrict)、
 * 追加 rules 変更不要。VerificationListPage は短時間滞在だが set 一覧と挙動を揃えて
 * onSnapshot で subscribe (config 編集を即座反映)。
 *
 * docs/04 §4-3 (DistrictAiVerificationConfig)、REQ-4.3.20〜25。
 */

export interface DistrictVerificationConfig {
  enabled: boolean;
  activeTemplateId: string | null;
  allowFreeVerification: boolean;
}

export type DistrictVerificationConfigState =
  | { status: 'loading'; config: null; error: null }
  | { status: 'ready'; config: DistrictVerificationConfig; error: null }
  | { status: 'error'; config: null; error: Error };

/** Firestore doc data から DistrictVerificationConfig を defensive narrow (pure、test 用に export)。 */
export function mapDistrictVerificationConfig(
  data: DocumentData | undefined,
): DistrictVerificationConfig {
  const aiv =
    (data?.aiVerification as
      | {
          enabled?: unknown;
          activeTemplateId?: unknown;
          allowFreeVerification?: unknown;
        }
      | undefined) ?? undefined;
  const enabled = typeof aiv?.enabled === 'boolean' ? aiv.enabled : false;
  const activeTemplateId =
    typeof aiv?.activeTemplateId === 'string' && aiv.activeTemplateId.length > 0
      ? aiv.activeTemplateId
      : null;
  const allowFreeVerification =
    typeof aiv?.allowFreeVerification === 'boolean' ? aiv.allowFreeVerification : false;
  return { enabled, activeTemplateId, allowFreeVerification };
}

export function useDistrictVerificationConfig(districtId: string): DistrictVerificationConfigState {
  const [state, setState] = useState<DistrictVerificationConfigState>({
    status: 'loading',
    config: null,
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({
        status: 'error',
        config: null,
        error: new Error('districtId is required'),
      });
      return;
    }
    const ref = doc(db, 'districts', districtId);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const config = mapDistrictVerificationConfig(snap.exists() ? snap.data() : undefined);
        setState({ status: 'ready', config, error: null });
      },
      (error) => setState({ status: 'error', config: null, error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}
